import React, { useCallback, useEffect, useState } from 'react';
import TallyService from '../../../services/TallyService';
import DeleteButton from '../../common/DeleteButton';
import { useTranslation } from 'react-i18next';

const getIncomeRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.incomeRecords)) return response.incomeRecords;
  if (Array.isArray(response?.incomes)) return response.incomes;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.incomeRecords)) return response.data.incomeRecords;
  if (Array.isArray(response?.data?.incomes)) return response.data.incomes;
  return [];
};

const IncomeManager = ({ schoolId, academicYearId, onToast, onIncomeAdded }) => {
  const { t } = useTranslation();
  const [incomeRows, setIncomeRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editingIncomeId, setEditingIncomeId] = useState(null);
  const [form, setForm] = useState({
    sourceName: '',
    category: '',
    amount: '',
    incomeDate: new Date().toISOString().slice(0, 10),
    description: '',
    receiptNumber: ''
  });

  const loadIncome = useCallback(async () => {
    try {
      setLoading(true);
      const response = await TallyService.getIncome(schoolId, academicYearId);
      setIncomeRows(getIncomeRows(response));
    } catch (error) {
      console.error('Error loading income:', error);
      onToast?.(t('errorLoadingIncome'), 'error');
    } finally {
      setLoading(false);
    }
  }, [schoolId, academicYearId, onToast, t]);

  useEffect(() => {
    loadIncome();
  }, [loadIncome]);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const incomeData = {
        ...form,
        amount: Number(form.amount)
      };
      if (editingIncomeId !== null) {
        await TallyService.updateIncome(editingIncomeId, incomeData);
        onToast?.(t('incomeUpdatedSuccess'), 'success');
      } else {
        await TallyService.addIncome(schoolId, academicYearId, incomeData);
        onToast?.(t('incomeAddedSuccess'), 'success');
      }
      setEditingIncomeId(null);
      setForm({
        sourceName: '',
        category: '',
        amount: '',
        incomeDate: new Date().toISOString().slice(0, 10),
        description: '',
        receiptNumber: ''
      });
      await loadIncome();
      onIncomeAdded?.();
    } catch (error) {
      console.error(editingIncomeId !== null ? 'Error updating income:' : 'Error adding income:', error);
      onToast?.(editingIncomeId !== null ? t('errorUpdatingIncome') : t('errorAddingIncome'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (incomeId) => {
    try {
      const response = await TallyService.getIncomeRecord(incomeId);
      const record = response?.data || response;
      setEditingIncomeId(incomeId);
      setForm({
        sourceName: record.sourceName || '',
        category: record.category || '',
        amount: record.amount ?? '',
        incomeDate: (record.incomeDate || record.date || '').slice(0, 10),
        description: record.description || '',
        receiptNumber: record.receiptNumber || ''
      });
    } catch (error) {
      console.error('Error loading income record:', error);
      onToast?.(t('errorLoadingIncome'), 'error');
    }
  };

  const handleDelete = async (incomeId) => {
    try {
      await TallyService.deleteIncome(incomeId);
      onToast?.(t('incomeDeletedSuccess'), 'success');
      if (editingIncomeId === incomeId) cancelEdit();
      await loadIncome();
      onIncomeAdded?.();
    } catch (error) {
      console.error('Error deleting income:', error);
      onToast?.(t('errorDeletingIncome'), 'error');
      throw error;
    }
  };

  const cancelEdit = () => {
    setEditingIncomeId(null);
    setForm({
      sourceName: '',
      category: '',
      amount: '',
      incomeDate: new Date().toISOString().slice(0, 10),
      description: '',
      receiptNumber: ''
    });
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await TallyService.exportIncome(schoolId, academicYearId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'tally-income.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      onToast?.(t('excelDownloadedSuccessfully'), 'success');
    } catch (error) {
      console.error('Error exporting income:', error);
      onToast?.(t('errorExportingExcel'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', minimumFractionDigits: 2
  }).format(Number(amount) || 0);

  const label = (key, fallback) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const totalIncome = incomeRows.reduce((total, row) => total + (Number(row.amount) || 0), 0);

  return (
    <section className="tally-income-page">
      <style>{`
        .tally-income-page {
          --income-ink: #172b4d;
          --income-muted: #64748b;
          --income-line: #e2e8f0;
          --income-primary: #153e75;
          display: grid;
          gap: 24px;
          padding: 24px;
          color: var(--income-ink);
        }
        .tally-income-page * { box-sizing: border-box; }
        .income-panel {
          min-width: 0;
          padding: 24px;
          background: #fff;
          border: 1px solid var(--income-line);
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(15, 23, 42, .045);
        }
        .income-panel-heading {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 22px;
        }
        .income-panel-heading h2 {
          margin: 0;
          color: var(--income-ink);
          font-size: 18px;
          font-weight: 700;
          letter-spacing: -.02em;
        }
        .income-panel-heading p {
          margin: 6px 0 0;
          color: var(--income-muted);
          font-size: 13px;
          line-height: 1.5;
        }
        .income-form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          column-gap: 20px;
        }
        .tally-income-page .tally-form-group { min-width: 0; margin-bottom: 18px; }
        .tally-income-page .tally-form-group label {
          display: block;
          margin-bottom: 7px;
          color: #334155;
          font-size: 13px;
          font-weight: 600;
        }
        .tally-income-page .tally-form-group input,
        .tally-income-page .tally-form-group textarea {
          width: 100%;
          min-height: 44px;
          padding: 10px 12px;
          color: var(--income-ink);
          background: #fff;
          border: 1px solid #cbd5e1;
          border-radius: 9px;
          font: inherit;
          font-size: 14px;
          transition: border-color .18s ease, box-shadow .18s ease;
        }
        .tally-income-page .tally-form-group textarea {
          min-height: 92px;
          resize: vertical;
        }
        .tally-income-page .tally-form-group input:hover,
        .tally-income-page .tally-form-group textarea:hover { border-color: #94a3b8; }
        .tally-income-page .tally-form-group input:focus,
        .tally-income-page .tally-form-group textarea:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, .13);
        }
        .income-form-wide { grid-column: 1 / -1; }
        .income-form-actions {
          display: flex;
          gap: 10px;
          margin-top: 2px;
        }
        .tally-income-page .income-primary-button,
        .tally-income-page .income-secondary-button,
        .tally-income-page .income-export-button {
          min-height: 42px;
          padding: 0 17px;
          border-radius: 9px;
          font: inherit;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background .18s ease, border-color .18s ease, transform .18s ease;
        }
        .tally-income-page .income-primary-button {
          color: #fff;
          background: var(--income-primary);
          border: 1px solid var(--income-primary);
        }
        .tally-income-page .income-primary-button:hover:not(:disabled) {
          background: #0f2f5d;
          transform: translateY(-1px);
        }
        .tally-income-page .income-secondary-button,
        .tally-income-page .income-export-button {
          color: #334155;
          background: #fff;
          border: 1px solid #cbd5e1;
        }
        .tally-income-page .income-secondary-button:hover:not(:disabled),
        .tally-income-page .income-export-button:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #94a3b8;
        }
        .tally-income-page button:disabled { cursor: not-allowed; opacity: .58; }
        .income-history-heading { align-items: center; margin-bottom: 16px; }
        .income-history-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 14px;
          flex-wrap: wrap;
        }
        .income-total {
          padding: 8px 12px;
          color: #116149;
          background: #ecfdf5;
          border: 1px solid #c7f0df;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
        }
        .tally-income-page .tally-table-container {
          overflow: auto;
          border: 1px solid var(--income-line);
          border-radius: 10px;
        }
        .tally-income-page .tally-table { min-width: 760px; }
        .tally-income-page .tally-table thead { background: #f8fafc; }
        .tally-income-page .tally-table th {
          padding: 13px 14px;
          color: #475569;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: .045em;
          text-transform: uppercase;
          white-space: nowrap;
        }
        .tally-income-page .tally-table td {
          padding: 13px 14px;
          color: #334155;
          font-size: 13px;
          vertical-align: middle;
        }
        .tally-income-page .tally-table tbody tr:last-child td { border-bottom: 0; }
        .tally-income-page .tally-table tbody tr:hover { background: #f8fafc; }
        .tally-income-page .income-amount {
          color: #116149 !important;
          font-weight: 700;
          white-space: nowrap;
        }
        .tally-income-page .income-row-actions { white-space: nowrap; }
        .tally-income-page .income-edit-button {
          width: 34px;
          height: 34px;
          margin-right: 6px;
          color: #315b9a;
          background: #eff6ff;
          border: 1px solid #dbeafe;
          border-radius: 8px;
          cursor: pointer;
        }
        .tally-income-page .income-edit-button:hover:not(:disabled) { background: #dbeafe; }
        .income-state {
          padding: 32px 16px;
          color: var(--income-muted);
          text-align: center;
          font-size: 14px;
        }
        @media (max-width: 700px) {
          .tally-income-page { gap: 16px; padding: 16px; }
          .income-panel { padding: 18px; border-radius: 12px; }
          .income-form-grid { grid-template-columns: minmax(0, 1fr); }
          .income-form-wide { grid-column: auto; }
          .income-history-heading { align-items: flex-start; flex-direction: column; }
          .income-history-actions { justify-content: flex-start; }
        }
        @media (max-width: 420px) {
          .tally-income-page { padding: 12px; }
          .income-panel { padding: 15px; }
          .income-form-actions { flex-direction: column; }
        }
      `}</style>

      <div className="income-panel">
        <div className="income-panel-heading">
          <div>
            <h2>{editingIncomeId ? label('updateIncome', 'Update income') : label('addIncome', 'Add income')}</h2>
            <p>Record and manage income received by your institution.</p>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="income-form-grid">
            <div className="tally-form-group">
              <label htmlFor="income-source">{label('sourceName', 'Income source')} *</label>
              <input id="income-source" name="sourceName" value={form.sourceName} onChange={handleChange} required disabled={saving} />
            </div>
            <div className="tally-form-group">
              <label htmlFor="income-category">{label('category', 'Category')} *</label>
              <input id="income-category" name="category" value={form.category} onChange={handleChange} required disabled={saving} />
            </div>
            <div className="tally-form-group">
              <label htmlFor="income-amount">{label('amount', 'Amount')} (₹) *</label>
              <input id="income-amount" name="amount" type="number" min="0.01" step="0.01" value={form.amount} onChange={handleChange} required disabled={saving} />
            </div>
            <div className="tally-form-group">
              <label htmlFor="income-date">{label('date', 'Date')} *</label>
              <input id="income-date" name="incomeDate" type="date" value={form.incomeDate} onChange={handleChange} required disabled={saving} />
            </div>
            <div className="tally-form-group">
              <label htmlFor="income-receipt">{label('receiptNumber', 'Receipt number')}</label>
              <input id="income-receipt" name="receiptNumber" value={form.receiptNumber} onChange={handleChange} disabled={saving} />
            </div>
            <div className="tally-form-group income-form-wide">
              <label htmlFor="income-description">{label('descriptionOptional', 'Description (optional)')}</label>
              <textarea id="income-description" name="description" value={form.description} onChange={handleChange} rows="3" disabled={saving} />
            </div>
          </div>
          <div className="income-form-actions">
            <button className="income-primary-button" type="submit" disabled={saving}>
              {saving ? label('saving', 'Saving…') : editingIncomeId ? label('updateIncome', 'Update income') : label('addIncome', 'Add income')}
            </button>
            {editingIncomeId && (
              <button className="income-secondary-button" type="button" onClick={cancelEdit} disabled={saving}>
                {label('cancel', 'Cancel')}
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="income-panel">
        <div className="income-panel-heading income-history-heading">
          <div>
            <h2>{label('incomeHistory', 'Income history')}</h2>
            <p>Review all recorded income and manage individual entries.</p>
          </div>
          <div className="income-history-actions">
            <span className="income-total">{label('total', 'Total')}: {formatCurrency(totalIncome)}</span>
            <button className="income-export-button" type="button" onClick={handleExport} disabled={exporting}>
              {exporting ? label('downloading', 'Preparing…') : label('exportToExcel', 'Export to Excel')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="income-state">{label('loadingTallyData', 'Loading income records…')}</div>
        ) : incomeRows.length === 0 ? (
          <div className="income-state">{label('noIncomeFound', 'No income records yet.')}</div>
        ) : (
          <div className="tally-table-container">
            <table className="tally-table">
              <thead>
                <tr>
                  <th>{label('date', 'Date')}</th><th>{label('sourceName', 'Income source')}</th><th>{label('category', 'Category')}</th>
                  <th>{label('receiptNumber', 'Receipt number')}</th><th>{label('amount', 'Amount')}</th><th>{label('descriptionOptional', 'Description')}</th><th>{label('actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {incomeRows.map((row, index) => (
                  <tr key={row.id || row.incomeId || index}>
                    <td>{row.incomeDate || row.date || '-'}</td>
                    <td>{row.sourceName || '-'}</td>
                    <td>{row.category || '-'}</td>
                    <td>{row.receiptNumber || '-'}</td>
                    <td className="income-amount">{formatCurrency(row.amount)}</td>
                    <td>{row.description || '-'}</td>
                    <td className="income-row-actions">
                      <button
                        className="income-edit-button"
                        type="button"
                        onClick={() => handleEdit(row.id || row.incomeId)}
                        aria-label={label('edit', 'Edit')}
                        title={label('edit', 'Edit')}
                        disabled={!row.id && !row.incomeId}
                      >
                        ✏️
                      </button>
                      <DeleteButton
                        objectId={row.id || row.incomeId}
                        objectName={row.sourceName || label('tallyIncome', 'Income')}
                        onDelete={handleDelete}
                        itemType={label('tallyIncome', 'Income')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default IncomeManager;